import { useEffect, useState } from 'react'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { MapPin } from 'lucide-react'

import { JobOpeningIdentityHeader } from '@/components/job-opening-identity'
import { JobOpeningExperiencesReadSection } from '@/components/job-opening-experiences-read-section'
import { JobOpeningSkillsReadSection } from '@/components/job-opening-skills-read-section'
import {
  useExternalApplyContext,
  useExternalApplyForm,
} from '@/components/external-apply/external-apply-form-state'
import { ExternalApplyFields } from '@/components/external-apply/external-apply-start-form'
import { CopyPublicLinkButton } from '@/components/public/copy-public-link-button'
import { PublicEntityHeader } from '@/components/public/public-entity-header'
import { PublicEntityMetadataRow } from '@/components/public/public-entity-metadata-row'
import { PublicPageHead } from '@/components/public/public-page-head'
import { PublicShareLayout } from '@/components/public/public-share-layout'
import { PUBLIC_CARD_SURFACE } from '@/components/public/public-surfaces'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { MarkdownBody } from '@/components/ui/markdown-content'
import { stripRedundantLeadingHeading } from '@/lib/markdown-display'
import { API_ORIGIN } from '@/constants'
import { usePublicShareRouteParam } from '@/lib/public-share-route-param'
import { isPublicOrganisationsEnabled } from '@/utils/candidate-organisation-path'
import type { PublicJob } from '@/types/public-share'
import { formatPublicJobMeta } from '@/utils/public-job-meta-display'

function PublicJobApplyButton({ href }: { href: string }) {
  return (
    <Button asChild size="sm" pressFeedback>
      <a href={href}>Apply now</a>
    </Button>
  )
}

export function PublicJobPage({ slugOverride }: { slugOverride?: string } = {}) {
  const slug = usePublicShareRouteParam('/jobs/:slug', 'slug', slugOverride)
  const applyContext = useExternalApplyContext(slug || undefined)
  const applyForm = useExternalApplyForm(
    slug || undefined,
    applyContext.status === 'ready' ? applyContext.context : null,
  )
  const [job, setJob] = useState<PublicJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const orgPublicEnabled = isPublicOrganisationsEnabled()

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!slug) {
        if (!cancelled) {
          setLoading(false)
          setError('Job not found')
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_ORIGIN}/api/public/opportunities/${encodeURIComponent(slug.trim())}`)
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Job not found')
        if (!cancelled) setJob(data as PublicJob)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Job not found')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <PublicShareLayout>
        <EntityDetailSkeleton variant="job" withPageContainer={false} ariaLabel="Loading job" />
      </PublicShareLayout>
    )
  }

  if (error || !job) {
    return (
      <PublicShareLayout>
        <Card className={PUBLIC_CARD_SURFACE}>
          <CardHeader>
            <CardTitle>Job unavailable</CardTitle>
            <CardDescription alwaysShow>{error || 'This job could not be loaded.'}</CardDescription>
          </CardHeader>
        </Card>
      </PublicShareLayout>
    )
  }

  const meta = formatPublicJobMeta(job)
  const descriptionBody = stripRedundantLeadingHeading(job.description, [job.title])
  const applyHref = job.apply_url || `/apply/${job.slug}`
  const shareUrl = job.share_url || `${window.location.origin}/jobs/${job.slug}`
  const boardUrl = job.board_url || job.detail_url || ''
  const showApplyHop = applyContext.status === 'missing'

  return (
    <PublicShareLayout>
      <PublicPageHead
        title={`${job.title} · ${job.organisation_name}`}
        description={job.description.slice(0, 200)}
        canonicalUrl={boardUrl || undefined}
        canonicalPath={boardUrl ? undefined : `/jobs/${job.slug}`}
      />
      <article className="space-y-8">
        <PublicEntityHeader
          clearance="none"
          title={
            <JobOpeningIdentityHeader
              presentation="public"
              title={job.title}
              organisationLabel={job.organisation_name}
              organisationLogoUrl={job.organisation_logo_url}
              organisationHref={
                orgPublicEnabled && job.organisation_slug?.trim()
                  ? `/o/${job.organisation_slug}`
                  : undefined
              }
              showPublicationBadge={false}
            />
          }
          actions={
            <>
              <CopyPublicLinkButton url={shareUrl} />
              {showApplyHop ? <PublicJobApplyButton href={applyHref} /> : null}
            </>
          }
        >
          {meta ? <PublicEntityMetadataRow icon={MapPin}>{meta}</PublicEntityMetadataRow> : null}
          {job.salary_display ? (
            <p className="text-sm font-semibold text-foreground">{job.salary_display}</p>
          ) : null}
        </PublicEntityHeader>
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">About this role</h2>
          <MarkdownBody
            className="!text-base leading-relaxed"
            plainClassName="text-base leading-relaxed text-muted-foreground"
          >
            {descriptionBody}
          </MarkdownBody>
        </section>
        <JobOpeningSkillsReadSection
          skills={job.skills ?? []}
          emptyMessage="No skills listed."
          presentation="plain"
        />
        <JobOpeningExperiencesReadSection experiences={job.experiences ?? []} />
        {showApplyHop ? <PublicJobApplyButton href={applyHref} /> : null}
        {applyContext.status === 'loading' ? (
          <p className="text-sm text-muted-foreground" role="status">
            Loading application form...
          </p>
        ) : null}
        {applyContext.status === 'error' ? (
          <p className="text-sm text-destructive" role="alert">
            {applyContext.message}
          </p>
        ) : null}
        {applyContext.status === 'ready' && applyForm.submittedEmail ? (
          <section className="space-y-2" role="status" aria-live="polite">
            <h2 className="text-xl font-semibold">Check your email</h2>
            <p className="text-sm text-muted-foreground">
              We sent a secure link to {applyForm.submittedEmail}. Use it to finish your application for{' '}
              {applyContext.context.opportunity.title}.
            </p>
          </section>
        ) : null}
        {applyContext.status === 'ready' && !applyForm.submittedEmail ? (
          <section className="space-y-6" aria-labelledby="public-job-apply-heading">
            <h2 id="public-job-apply-heading" className="text-xl font-semibold">
              Apply for this role
            </h2>
            <ExternalApplyFields form={applyForm} />
          </section>
        ) : null}
      </article>
    </PublicShareLayout>
  )
}
